import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { DOCUMENT } from '../../constants/documentTheme';
import DeveloperAccessCallout from '../../components/DeveloperAccessCallout';

const PARTNER_AUTH = { usePartnerAuth: true };

const DeveloperWebhooks = () => {
  const { t } = useTranslation();
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [url, setUrl] = useState('');
  const [environment, setEnvironment] = useState('test');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [newSecret, setNewSecret] = useState(null);

  const loadEntitlements = useCallback(async () => {
    try {
      const planRes = await api.get('/partners/plan', PARTNER_AUTH);
      setEntitlements(planRes.data?.entitlements || null);
    } catch {
      // ignore
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setAccessBlocked(false);
      const res = await api.get('/partners/developer/webhooks', PARTNER_AUTH);
      setWebhooks(res.data?.webhooks || []);
      setEvents(res.data?.events || []);
      if (!selectedEvents.length && res.data?.events?.length) {
        setSelectedEvents(res.data.events);
      }
    } catch (err) {
      const code = err.response?.data?.error;
      const message = err.response?.data?.message || err.response?.data?.error || t('developerPortal.errors.loadFailed');
      if (code === 'partner_feature_limit' || err.response?.status === 403) {
        setAccessBlocked(true);
        setError(typeof message === 'string' ? message : t('developerPortal.home.noApi'));
        if (err.response?.data?.entitlements) {
          setEntitlements(err.response.data.entitlements);
        }
      } else {
        setError(typeof message === 'string' ? message : t('developerPortal.errors.loadFailed'));
      }
      setWebhooks([]);
    } finally {
      setLoading(false);
    }
  }, [t, selectedEvents.length]);

  const refresh = useCallback(async () => {
    await loadEntitlements();
    await load();
  }, [loadEntitlements, load]);

  useEffect(() => {
    loadEntitlements();
    load();
  }, [loadEntitlements, load]);

  const toggleEvent = (eventName) => {
    setSelectedEvents((prev) => (
      prev.includes(eventName)
        ? prev.filter((e) => e !== eventName)
        : [...prev, eventName]
    ));
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError('');
      const res = await api.post(
        '/partners/developer/webhooks',
        { url, environment, events: selectedEvents },
        PARTNER_AUTH
      );
      setNewSecret(res.data?.webhook?.signing_secret || null);
      setUrl('');
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || t('developerPortal.errors.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('developerPortal.webhooks.confirmDelete'))) return;
    try {
      await api.delete(`/partners/developer/webhooks/${id}`, PARTNER_AUTH);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('developerPortal.errors.deleteFailed'));
    }
  };

  const handleTest = async (id) => {
    try {
      await api.post(`/partners/developer/webhooks/${id}/test`, {}, PARTNER_AUTH);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || t('developerPortal.errors.testFailed'));
    }
  };

  const features = entitlements?.features || {};
  const canUseApi = Boolean(features.hasApiSandbox || features.hasProductionApi);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 34 }, mb: 0.75 }}>
            {t('developerPortal.webhooks.title')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink2, maxWidth: '58ch' }}>
            {t('developerPortal.webhooks.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          disableElevation
          onClick={() => setDialogOpen(true)}
          disabled={!canUseApi}
          sx={{ bgcolor: DOCUMENT.oxblood, borderRadius: 0, textTransform: 'none', '&:hover': { bgcolor: DOCUMENT.oxblood } }}
        >
          {t('developerPortal.webhooks.create')}
        </Button>
      </Box>

      {(accessBlocked || !canUseApi) ? (
        <DeveloperAccessCallout
          entitlements={entitlements}
          onEntitlementsChange={refresh}
          errorMessage={error || undefined}
        />
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{String(error)}</Alert>
      ) : null}
      {newSecret ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNewSecret(null)}>
          {t('developerPortal.webhooks.secretOnce', { secret: newSecret })}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      ) : webhooks.length === 0 ? (
        <Typography sx={{ color: DOCUMENT.ink3 }}>{t('developerPortal.webhooks.empty')}</Typography>
      ) : (
        <Table size="small" sx={{ bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}` }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('developerPortal.webhooks.colUrl')}</TableCell>
              <TableCell>{t('developerPortal.webhooks.colEnv')}</TableCell>
              <TableCell>{t('developerPortal.webhooks.colEvents')}</TableCell>
              <TableCell align="right">{t('developerPortal.webhooks.colActions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {webhooks.map((hook) => (
              <TableRow key={hook.id}>
                <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{hook.url}</TableCell>
                <TableCell>{hook.environment}</TableCell>
                <TableCell>{Array.isArray(hook.events) ? hook.events.length : '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => handleTest(hook.id)} sx={{ textTransform: 'none' }}>
                    {t('developerPortal.webhooks.test')}
                  </Button>
                  <Button size="small" color="error" onClick={() => handleDelete(hook.id)} sx={{ textTransform: 'none' }}>
                    {t('developerPortal.webhooks.delete')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => !creating && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('developerPortal.webhooks.createTitle')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('developerPortal.webhooks.urlLabel')}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            fullWidth
            required
            placeholder="https://"
          />
          <FormControl fullWidth>
            <InputLabel id="wh-env">{t('developerPortal.webhooks.envLabel')}</InputLabel>
            <Select
              labelId="wh-env"
              label={t('developerPortal.webhooks.envLabel')}
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
            >
              <MenuItem value="test">{t('developerPortal.keys.envTest')}</MenuItem>
              <MenuItem value="live">{t('developerPortal.keys.envLive')}</MenuItem>
            </Select>
          </FormControl>
          <FormGroup>
            {events.map((eventName) => (
              <FormControlLabel
                key={eventName}
                control={(
                  <Checkbox
                    checked={selectedEvents.includes(eventName)}
                    onChange={() => toggleEvent(eventName)}
                  />
                )}
                label={eventName}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating} sx={{ textTransform: 'none' }}>
            {t('developerPortal.common.cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !url.trim()}
            variant="contained"
            sx={{ textTransform: 'none', bgcolor: DOCUMENT.oxblood }}
          >
            {creating ? <CircularProgress size={18} color="inherit" /> : t('developerPortal.webhooks.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeveloperWebhooks;
